"use client"

import React, { useState, useEffect, useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { FormField } from '@/shared/components/ui/form'
import FormSelectInput from '../../../../shared/components/form-inputs/form-select-input'
import FormMultiSelectInput from '../../../../shared/components/form-inputs/form-multi-select-input'
import FormTextInputWrapper from '@/shared/components/form-inputs/form-input-wrapper'
import { Button } from '../../../../shared/components/ui/button'
import { Input } from '@/shared/components/ui/new-input'
import {
    getWorkOrderQueryParamsSchema,
    GetWorkOrderQueryParamsType,
    WORK_ORDERS_LIST_DEFAULT_LIMIT,
} from '../../schemas/query-params.schema'
import { DialogClose } from '@/shared/components/ui/dialog'
import { buildUrl } from '@/shared/lib/utils'
import { cn } from '@/shared/lib/utils'
import { useRouter, usePathname } from 'next/navigation'
import { useCurrentSearchParams } from '@/shared/hooks/use-current-search-params'
import { getAllWorkOrders } from '../../actions'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { WORK_ORDER_TYPE_OPTIONS_LIST } from '../../constants/select-options'
import { useSession } from '../../../auth/context/session.context'

type WorkOrdersFilterFormProps = {
    onSuccessCallback?: () => void,
}

const SUGGESTIONS_MAX = 80

// Type-to-search combobox: single string value, suggestions list (like column filters)
function SearchableCombobox({
    value,
    onChange,
    suggestions,
    placeholder = "Type to search…",
}: {
    value: string
    onChange: (v: string) => void
    suggestions: string[]
    placeholder?: string
}) {
    const [isListOpen, setIsListOpen] = useState(false)
    const filtered = useMemo(() => {
        const q = value.trim().toLowerCase()
        const list = q
            ? suggestions.filter((n) => n.toLowerCase().includes(q))
            : suggestions
        return list.slice(0, SUGGESTIONS_MAX)
    }, [suggestions, value])
    const showList = isListOpen && filtered.length > 0

    return (
        <div className="space-y-1.5">
            <Input
                value={value}
                onChange={(e) => {
                    onChange((e.target as HTMLInputElement).value)
                    setIsListOpen(true)
                }}
                onFocus={() => {
                    // Only open list on focus when user has already entered a value (e.g. to change selection).
                    // When empty, avoid opening on dialog load / auto-focus.
                    if (value.trim()) setIsListOpen(true)
                }}
                onBlur={() => setTimeout(() => setIsListOpen(false), 150)}
                placeholder={placeholder}
                className="h-9 rounded-full"
            />
            {showList && (
                <div className="border rounded-md bg-muted/50 max-h-40 overflow-y-auto">
                    {filtered.map((name) => (
                        <button
                            key={name}
                            type="button"
                            className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
                            onMouseDown={(e) => {
                                e.preventDefault()
                                onChange(name)
                                setIsListOpen(false)
                            }}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

// Owner combobox: form stores ownerId, display is owner fullname (like pic 2)
function OwnerCombobox({
    value: ownerId,
    onChange,
    options,
}: {
    value: string
    onChange: (id: string) => void
    options: { value: string; label: string }[]
}) {
    const [isListOpen, setIsListOpen] = useState(false)
    const [localQuery, setLocalQuery] = useState<string>("")
    useEffect(() => {
        setLocalQuery("")
    }, [ownerId])
    const selectedLabel = options.find((o) => o.value === ownerId)?.label ?? ""
    const displayText = localQuery !== "" ? localQuery : selectedLabel

    const filtered = useMemo(() => {
        const q = displayText.trim().toLowerCase()
        const list = q
            ? options.filter((o) => o.label.toLowerCase().includes(q))
            : options
        return list.slice(0, SUGGESTIONS_MAX)
    }, [options, displayText])
    const showList = isListOpen && filtered.length > 0

    return (
        <div className="space-y-1.5">
            <Input
                value={displayText}
                onChange={(e) => {
                    setLocalQuery((e.target as HTMLInputElement).value)
                    setIsListOpen(true)
                }}
                onFocus={() => {
                    // Only open list on focus when there is already a selection (so user can change it).
                    if (selectedLabel.trim()) setIsListOpen(true)
                }}
                onBlur={() => {
                    setTimeout(() => {
                        setIsListOpen(false)
                        setLocalQuery("")
                    }, 150)
                }}
                placeholder="Type to search owner…"
                className="h-9 rounded-full"
            />
            {showList && (
                <div className="border rounded-md bg-muted/50 max-h-40 overflow-y-auto">
                    {filtered.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
                            onMouseDown={(e) => {
                                e.preventDefault()
                                onChange(opt.value)
                                setLocalQuery("")
                                setIsListOpen(false)
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

// Open/Closed only (matches WO cards toggle); labels "Open"/"Closed", values stay Active/Inactive for API
const statusOptions = [
    { value: 'Active', label: 'Open' },
    { value: 'Inactive', label: 'Closed' },
]

function WorkOrdersFilterForm({ onSuccessCallback }: WorkOrdersFilterFormProps) {
    const [teamNameOptions, setTeamNameOptions] = useState<{ value: string; label: string }[]>([])
    const [ownerOptions, setOwnerOptions] = useState<{ value: string; label: string }[]>([])
    const [teamLeadOptions, setTeamLeadOptions] = useState<{ value: string; label: string }[]>([])
    const [homeownerNameSuggestions, setHomeownerNameSuggestions] = useState<string[]>([])
    const { regionId } = useRegionId()
    const { session } = useSession()

    const currentParams = useCurrentSearchParams<GetWorkOrderQueryParamsType>([
        "status",
        "homeownerName",
        "city",
        "street",
        "workOrderType",
        "ownerId",
        "teamName",
        "delegateTo",
        "createdDate",
    ])

    // Normalize workOrderType to array for multiselect; teamName to string
    const normalizedParams = useMemo(() => ({
        ...currentParams,
        workOrderType: Array.isArray(currentParams.workOrderType)
            ? currentParams.workOrderType
            : currentParams.workOrderType
                ? [currentParams.workOrderType]
                : [],
        teamName: Array.isArray(currentParams.teamName)
            ? currentParams.teamName?.[0] ?? undefined
            : currentParams.teamName,
    }), [currentParams])

    const form = useForm<GetWorkOrderQueryParamsType & { workOrderType?: string[] }>({
        resolver: zodResolver(getWorkOrderQueryParamsSchema) as any,
        mode: "onSubmit",
        defaultValues: normalizedParams,
    })
    const { control, formState: { isSubmitting } } = form

    // Sync form with URL when params change (e.g. dialog reopened after URL change).
    // Use a string signature so we only reset when param values change, not on every render
    // (useCurrentSearchParams returns a new object each time, which would cause infinite loop).
    const paramsSignature = JSON.stringify(normalizedParams)
    useEffect(() => {
        form.reset(normalizedParams)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when params signature changes
    }, [paramsSignature])
    const router = useRouter()
    const pathname = usePathname()

    // Fetch options for Team Name, Owners, Team Lead, Homeowner Name
    useEffect(() => {
        const fetchOptions = async () => {
            if (!regionId) {
                setTeamNameOptions([])
                setOwnerOptions([])
                setTeamLeadOptions([])
                setHomeownerNameSuggestions([])
                return
            }
            try {
                const response = await getAllWorkOrders({
                    limit: 1000,
                    regionId,
                })
                if (response.success && response.data?.workOrders) {
                    const list = response.data.workOrders as any[]

                    const teamNames = Array.from(
                        new Set(list.map((wo) => wo.teamName?.trim()).filter(Boolean))
                    ).sort((a, b) => (a as string).localeCompare(b as string))
                    setTeamNameOptions([
                        { value: "Not assigned", label: "Not assigned" },
                        ...teamNames.map((name) => ({ value: name as string, label: name as string }))
                    ])

                    const ownerMap = new Map<number, string>()
                    list.forEach((wo) => {
                        if (wo.owner?.id && wo.owner?.fullname)
                            ownerMap.set(wo.owner.id, wo.owner.fullname)
                    })
                    setOwnerOptions(
                        Array.from(ownerMap.entries())
                            .map(([id, fullname]) => ({ value: String(id), label: fullname }))
                            .sort((a, b) => a.label.localeCompare(b.label))
                    )

                    const teamLeadSet = new Set(
                        list.map((wo) => wo.teamLead?.fullname?.trim()).filter(Boolean)
                    )
                    teamLeadSet.add("Not assigned")
                    setTeamLeadOptions(
                        Array.from(teamLeadSet)
                            .sort((a, b) => a.localeCompare(b))
                            .map((name) => ({ value: name, label: name }))
                    )

                    const hoNames = Array.from(
                        new Set(
                            list
                                .map((wo) => wo.home?.homeowner?.user?.fullname?.trim())
                                .filter(Boolean)
                        )
                    ).sort((a, b) => (a as string).localeCompare(b as string))
                    setHomeownerNameSuggestions(hoNames as string[])
                }
            } catch (error) {
                console.error('Error fetching filter options:', error)
            }
        }

        fetchOptions()
    }, [regionId, session?.role])

    const onSubmit = (data: GetWorkOrderQueryParamsType & { workOrderType?: string[] }) => {
        try {
            const workOrderType = Array.isArray(data.workOrderType) && data.workOrderType.length
                ? data.workOrderType
                : undefined
            const payload = {
                ...data,
                workOrderType,
                teamName: data.teamName ?? undefined,
            }
            const filterData = Object.fromEntries(
                Object.entries(payload).filter(([key, value]) =>
                    value !== undefined &&
                    value !== null &&
                    value !== '' &&
                    !(Array.isArray(value) && value.length === 0) &&
                    !['page', 'limit', 'sortBy', 'sortOrder'].includes(key)
                )
            ) as Record<string, string | number | string[]>

            const url = buildUrl(
                pathname,
                filterData,
                {
                    previousParams: {
                        page: 1,
                        limit: WORK_ORDERS_LIST_DEFAULT_LIMIT,
                    }
                }
            )
            router.push(url)
            onSuccessCallback?.()
        } catch (error) {
            console.error("Error building URL or pushing to router:", error)
        }
    }

    const onClear = () => {
        try {
            form.reset({
                status: undefined,
                homeownerName: undefined,
                city: undefined,
                street: undefined,
                workOrderType: [],
                ownerId: undefined,
                teamName: undefined,
                delegateTo: undefined,
                createdDate: undefined,
            })

            const url = buildUrl(
                pathname,
                {},
                {
                    previousParams: {
                        page: 1,
                        limit: WORK_ORDERS_LIST_DEFAULT_LIMIT,
                    }
                }
            )
            router.push(url)
            onSuccessCallback?.()
        } catch (error) {
            console.error("Error clearing filters:", error)
        }
    }

    const onError = (errors: any) => {
        console.log("Form validation errors:", errors)
    }

    return (
        <FormProvider {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onError)}
                className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 [&_input]:rounded-full [&_[role=combobox]]:rounded-full'
            >
                <div className="space-y-2 w-full min-w-0 lg:col-span-2">
                    <label className="text-sm font-medium leading-none">Homeowner Name</label>
                    <Controller
                        name="homeownerName"
                        control={control}
                        render={({ field }) => (
                            <SearchableCombobox
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                suggestions={homeownerNameSuggestions}
                                placeholder="Type to search names…"
                            />
                        )}
                    />
                </div>

                <FormMultiSelectInput
                    name='workOrderType'
                    options={WORK_ORDER_TYPE_OPTIONS_LIST}
                    placeholder='Select work order types...'
                    triggerClassName="rounded-full"
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Work Order"
                    }}
                    control={control}
                />

                <div className="space-y-2 w-full min-w-0">
                    <label className="text-sm font-medium leading-none">Homeowner City</label>
                    <Controller
                        name="city"
                        control={control}
                        render={({ field }) => (
                            <Input
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                                placeholder="Filter by city…"
                                className="h-9 mt-0.5 w-full rounded-full"
                            />
                        )}
                    />
                </div>

                <div className="space-y-2 w-full min-w-0">
                    <label className="text-sm font-medium leading-none">Homeowner Street</label>
                    <Controller
                        name="street"
                        control={control}
                        render={({ field }) => (
                            <Input
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                                placeholder="Filter by street…"
                                className="h-9 mt-0.5 w-full rounded-full"
                            />
                        )}
                    />
                </div>

                <div className="space-y-2 w-full min-w-0">
                    <label className="text-sm font-medium leading-none">Workorder Owner</label>
                    <Controller
                        name="ownerId"
                        control={control}
                        render={({ field }) => (
                            <OwnerCombobox
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                options={ownerOptions}
                            />
                        )}
                    />
                </div>

                <div className="space-y-2 w-full min-w-0">
                    <label className="text-sm font-medium leading-none">Team Name</label>
                    <Controller
                        name="teamName"
                        control={control}
                        render={({ field }) => (
                            <SearchableCombobox
                                value={Array.isArray(field.value) ? field.value[0] ?? "" : (field.value ?? "")}
                                onChange={field.onChange}
                                suggestions={teamNameOptions.map((o) => o.label)}
                                placeholder="Type to search…"
                            />
                        )}
                    />
                </div>

                <div className="space-y-2 w-full min-w-0">
                    <label className="text-sm font-medium leading-none">Team Lead</label>
                    <Controller
                        name="delegateTo"
                        control={control}
                        render={({ field }) => (
                            <SearchableCombobox
                                value={field.value ?? ""}
                                onChange={field.onChange}
                                suggestions={teamLeadOptions.map((o) => o.label)}
                                placeholder="Type to search…"
                            />
                        )}
                    />
                </div>

                <FormField
                    control={control}
                    name="createdDate"
                    render={({ field, fieldState }) => (
                        <FormTextInputWrapper
                            inputGroupProps={{
                                wrapperClassName: 'w-full min-w-0',
                                label: 'Created Date',
                            }}
                        >
                            <div className="relative w-full">
                                <Input
                                    type="date"
                                    ref={field.ref}
                                    value={field.value ?? ''}
                                    onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                                    onBlur={field.onBlur}
                                    className={cn(
                                        'h-9 w-full rounded-full border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
                                        fieldState.error && 'border-destructive',
                                        !field.value && 'text-transparent'
                                    )}
                                />
                                {!field.value && (
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm text-muted-foreground">
                                        Select date
                                    </span>
                                )}
                            </div>
                        </FormTextInputWrapper>
                    )}
                />

                <FormSelectInput
                    name='status'
                    size="sm"
                    options={statusOptions}
                    placeholder='Select status...'
                    className="rounded-full"
                    inputGroupProps={{
                        wrapperClassName: 'w-full min-w-0',
                        label: "Open/Closed"
                    }}
                    control={control}
                />

                <div className='col-span-1 sm:col-span-2 lg:col-span-3 flex flex-col-reverse sm:flex-row gap-2 justify-end pt-4'>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onClear}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto"
                    >
                        Clear Filters
                    </Button>
                    <DialogClose asChild>
                        <Button type="button" variant="outline" className="w-full sm:w-auto">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                        {isSubmitting ? 'Applying...' : 'Apply Filters'}
                    </Button>
                </div>
            </form>
        </FormProvider>
    )
}

export default WorkOrdersFilterForm
